import axios from 'axios';
import { API_BASE_URL as BASE } from './apiConfig';

// ALT 텍스트 자동 생성 요청 타입
export interface DiagnoseFixOptions {
  language?: string;
  includeSeoKeywords?: boolean;
  userContext?: string;
  useContextProfile?: boolean;
}
export interface DiagnoseFixRequest {
  imageIds: number[];
  options?: DiagnoseFixOptions;
}
// ─── SEO 상세 결과 타입 추가 ─────────────
export interface PsiDetails {
  lcp: number | null;
  imageOptimizationScore: number | null;
  unusedImagesScore: number | null;
}
export interface MetaChecks {
  hasOgImage: boolean;
  hasSchemaImageObject: boolean;
  ogImageUrl: string | null;
}
export interface IssuesSummary {
  error: number;
  warning: number;
  info: number;
}
export interface DiagnoseSeoItem {
  code: string;
  severity: string;
  message: string;
  guide: string;
  scoreImpact: number;
}
export interface DiagnoseSeoResponse {
  /** B/E 기본 점수 */
  psiScore: number | null;
  psiDetails: PsiDetails | null;
  metaChecks: MetaChecks | null;
  issuesSummary: IssuesSummary | null;
  /** B/E SEO 항목 목록 */
  items: DiagnoseSeoItem[];
  /** 하위 호환: FE 내부에서 사용하던 필드 */
  score?: number;
  detail?: SeoDetail;
}

// ─── 개선: 점수 산정 근거, 24개 항목, PSI 메시지 타입 추가 ─────────────
export interface DiagnoseSeoScoreDetail {
  code: string; // 예: 'SEO001', 'SEO002'
  type?: string;
  category?: 'SEO' | 'PAGE_STRUCTURE' | string;
  message: string; // 이슈 설명
  count?: number;
  deduction: number; // 감점 점수
  severity?: 'success' | 'info' | 'warning' | 'error' | string;
  guide?: string; // 해결 가이드
  psiMessage?: string; // 구글 PSI/Lighthouse 상세 메시지
}

export interface DiagnoseA11yScoreDetail {
  kwcagId: string; // 예: '1.1.1'
  name: string; // 항목명
  result: 'PASS' | 'FAIL' | 'NOT_CHECKED' | 'INCOMPLETE';
  deduction: number; // 감점 점수 (FAIL 시 양수, 그 외 0)
  reason?: string; // 실패 사유
  guide?: string; // 해결 가이드
  manualGuide?: string; // NOT_CHECKED 수동 확인 방법
  toolLink?: string;   // NOT_CHECKED 추천 도구 링크
  totalCount?: number; // 총 이슈 수
  otherIssues?: string[]; // 나머지 이슈 HTML 코드 목록
}

export interface NotCheckedGuide {
  ruleId: string;
  reason: string;      // 자동 검사 불가 사유
  manualGuide: string; // 수동 확인 방법
  toolLink?: string;   // 추천 도구 링크
}

export interface DiagnoseDeductionSummary {
  appliedA11yDeduction: number;
  appliedSeoDeduction: number;
  appliedTotalDeduction: number;
}

export interface DiagnoseScoreDetailsResponse {
  seoScoreDetails: DiagnoseSeoScoreDetail[];
  a11yScoreDetails: DiagnoseA11yScoreDetail[];
  deductionSummary?: DiagnoseDeductionSummary;
  notCheckedGuides?: NotCheckedGuide[];
}

const API_BASE_URL = `${BASE}/api/diagnose`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DiagnoseProgress {
  step: string;
  percent: number;
}
export interface DiagnoseStatusResponse {
  jobId: string;
  status: DiagnoseStatus;
  progress: DiagnoseProgress | null;
  startedAt: string | null;
  lastUpdated: string | null;
}

export type DiagnoseStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
export type IssueSeverity = 'error' | 'warning' | 'info';
export type DiagnoseStartErrorCode = 'RATE_LIMIT_EXCEEDED' | 'URL_ACCESS_DENIED' | 'URL_INVALID';

export interface DiagnoseStartResponse {
  jobId: string;
  guestToken: string | null;
  version: number;
  status: DiagnoseStatus;
  estimatedSeconds: number;
  /** 에러 시 서버가 내려주는 코드 */
  errorCode: DiagnoseStartErrorCode | null;
  /** errorCode와 함께 오는 안내 메시지 */
  message: string | null;
}

export interface DiagnoseSummary {
  totalImages: number;
  imagesWithAlt: number;
  imagesMissingAlt: number;
  fixableByAlttext: number;
  impactMessage: string;
  a11yIssueCount: number;
  seoIssueCount: number;
  tabCounts?: {
    images: number;
    a11y: number;
    seo: number;
  };
  deductionSummary?: {
    a11y: number;
    seo: number;
  };
}

export type DiagnoseErrorCode = 'JOB_AUTO_CREATED' | 'JOB_NOT_FOUND' | 'DIAGNOSIS_FAILED' | 'RATE_LIMIT_EXCEEDED' | 'URL_ACCESS_DENIED' | 'URL_INVALID' | 'UNEXPECTED_ERROR';

export interface DiagnoseJobResponse {
  jobId: string;
  parentJobId: string | null;
  targetUrl: string;
  version: number;
  status: DiagnoseStatus;
  scoreAccessibility: number | null;
  scoreSeo: number | null;
  /** 실제 적용된 접근성 감점 (100 - scoreAccessibility) */
  totalA11yDeduction: number | null;
  /** 실제 적용된 SEO 감점 (100 - scoreSeo) */
  totalSeoDeduction: number | null;
  summary: DiagnoseSummary | null;
  errorMessage: string | null;
  /** 자동 생성/미발견 시 서버가 내려주는 코드 */
  errorCode: DiagnoseErrorCode | null;
  /** 예상 소요 시간 (초) */
  estimatedSeconds: number | null;
  /** errorCode와 함께 오는 안내 메시지 */
  message: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface ImageIssue {
  ruleId: string;
  severity: IssueSeverity;
  message: string;
}

export interface AiQualityDetails {
  specificity: number;
  specificityReason: string | null;
  keywordInclusion: number;
  keywordReason: string | null;
  wcagCompliance: number;
  wcagReason: string | null;
  lengthAppropriate: number;
  lengthReason: string | null;
  contextRelevance: number;
  contextReason: string | null;
  overallScore: number;
  overallReason: string | null;
}

export interface DiagnoseImageResponse {
  id: number;
  src: string;
  altText: string | null;
  fileName: string | null;
  fileFormat: string | null;
  hasLazyLoading: boolean;
  isInLink: boolean;
  isDecorative: boolean;
  a11yIssues: ImageIssue[];
  seoIssues: ImageIssue[];
  aiQualityScore: number | null;
  aiQualityReason: string | null;
  aiQualityDetails: AiQualityDetails | null;
  a11yScoreImpact: number;
  seoScoreImpact: number;
}

export interface DiagnoseImagePageResponse {
  content: DiagnoseImageResponse[];
  totalPages: number;
  totalElements: number;
  number: number;
}

export interface ManualCheckItem {
  ruleId: string;
  kwcagId: string;
  kwcagName: string;
  description: string;
  manualCheckGuide: string;
}

export interface CategorySummary {
  category: string;
  issueCount: number;
  scoreImpact: number;
}

export interface DiagnoseA11yResult {
  id: number;
  category: string;
  ruleId: string;
  severity: IssueSeverity;
  element: string | null;
  elementPath: string | null;
  message: string;
  guide: string | null;
  codeBefore: string | null;
  codeAfter: string | null;
  scoreImpact: number;
  kwcagId: string | null;
  kwcagName: string | null;
  kwcagLevel?: string;
  autoDetectable?: boolean;
  impact?: { legalRisk: string; legalBasis: string; affectedUsers: string[]; estimatedImpactPercent: number };
}

export interface KwcagCoverage {
  autoChecked: number;
  manualRequired: number;
  totalKwcag: number;
}

export interface DiagnoseA11yResponse {
  /** B/E는 items 필드로 반환 */
  items: DiagnoseA11yResult[];
  /** 하위 호환용 alias */
  results?: DiagnoseA11yResult[];
  totalCount?: number;
  kwcagCoverage: KwcagCoverage | number | null;
  manualCheckItems: ManualCheckItem[];
  byCategory: CategorySummary[];
}

export interface SeoDetail {
  altCompletionRate: number;
  hasOgImage: boolean;
  hasSchemaOrg: boolean;
  hasWebP: boolean;
  lazyLoadingRate: number;
  psiScore: number | null;
  /** 구조화 데이터 상세 분석 (신규) */
  structuredData?: {
    hasStructuredData: boolean;
    schemaTypes: string[];         // 감지된 schema type 목록 (예: ['Product', 'ImageObject'])
    scriptCount: number;
    hasImageObject: boolean;
    hasProductSchema: boolean;
    hasArticleSchema: boolean;
    hasOrganizationSchema: boolean;
    completionRate: number;        // 0~100
  };
  /** 이미지 포맷 비율 분석 (신규) */
  imageFormatAnalysis?: {
    totalImages: number;
    webpImages: number;
    jpegImages: number;
    pngImages: number;
    gifImages: number;
    webpRatio: number;             // 0~100
    modernFormatRatio: number;     // 0~100
  };
  /** Lazy Loading 적용률 분석 (신규) */
  lazyLoadingAnalysis?: {
    totalImages: number;
    lazyImages: number;
    eagerImages: number;
    lazyRatio: number;             // 0~100
    optimizationLevel: string;     // '우수' | '양호' | '개선필요' 등 한글
  };
}

export interface DiagnoseSummary {
  totalImages: number;
  imagesWithAlt: number;
  imagesMissingAlt: number;
  fixableByAlttext: number;
  impactMessage: string;
  a11yIssueCount: number;
  seoIssueCount: number;
  tabCounts?: {
    images: number;
    a11y: number;
    seo: number;
  };
  deductionSummary?: {
    a11y: number;
    seo: number;
  };
}

export interface DiagnoseFixResponse {
  queuedCount: number;
  remainingCredits: number;
  enqueuedImages: Array<{
    diagnoseImageId: number;
    imageUrl: string;
    crawlerImageId: number;
  }>;
  remainingImages: number;
  redirectUrl: string;
}

export interface DiagnoseClaimResponse {
  claimed: boolean;
  redirectUrl: string;
}

export interface DiagnoseHistoryItem {
  jobId: string;
  targetUrl: string;
  version: number;
  isLatest: boolean;
  status: DiagnoseStatus;
  scores: { accessibility: number | null; seo: number | null };
  previousScores: { accessibility: number | null; seo: number | null } | null;
  scoreChange: { accessibility: number | null; seo: number | null } | null;
  fixableByAlttext: number;
  claimedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface DiagnoseHistoryResponse {
  content: DiagnoseHistoryItem[];
  totalPages: number;
  totalElements: number;
  number: number;
}

export interface DiagnoseVersionHistoryItem {
  jobId: string;
  version: number;
  status: DiagnoseStatus;
  scores: { accessibility: number | null; seo: number | null };
  scoreChange: { accessibility: number | null; seo: number | null } | null;
  completedAt: string | null;
  createdAt: string;
}

// ─── Axios Instance ───────────────────────────────────────────────────────────

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── API Methods ──────────────────────────────────────────────────────────────

export const diagnoseApi = {
  /** 진단 시작 (비회원/회원 공통, parentJobId 있으면 재진단) */
  start: async (targetUrl: string, parentJobId?: string): Promise<DiagnoseStartResponse> => {
    const response = await apiClient.post('', { targetUrl, parentJobId });
    return response.data;
  },

  /** 진단 상태 폴링 (targetUrl 전달 시 JOB_NOT_FOUND일 경우 자동 재생성) */
  getJob: async (jobId: string, targetUrl?: string): Promise<DiagnoseJobResponse> => {
    const params = targetUrl ? { targetUrl } : undefined;
    const response = await apiClient.get(`/${jobId}`, { params });
    return response.data;
  },

  /** 이미지별 결과 (페이지네이션) */
  getImages: async (jobId: string, page = 0, size = 20, filter?: string): Promise<DiagnoseImagePageResponse> => {
    const params: any = { page, size };
    if (filter) params.filter = filter;
    const response = await apiClient.get(`/${jobId}/images`, { params });
    return response.data;
  },

  /** 접근성 상세 결과 (KWCAG 2.2) */
  getA11y: async (jobId: string): Promise<DiagnoseA11yResponse> => {
    const response = await apiClient.get(`/${jobId}/a11y`);
    return response.data;
  },

  /** SEO 상세 결과 */
  getSeo: async (jobId: string): Promise<DiagnoseSeoResponse> => {
    const response = await apiClient.get(`/${jobId}/seo`);
    return response.data;
  },

  /** ALT 텍스트 자동 생성 (회원 전용, 비회원은 guestToken 헤더로 호출) */
  fix: async (jobId: string, body: DiagnoseFixRequest, guestToken?: string): Promise<DiagnoseFixResponse> => {
    const headers: Record<string, string> = {};
    if (guestToken) headers['X-Guest-Token'] = guestToken;
    const response = await apiClient.post(`/${jobId}/fix`, body, { headers });
    return response.data;
  },

  /** 비회원 진단 결과 회원 귀속 */
  claim: async (jobId: string, guestToken: string): Promise<DiagnoseClaimResponse> => {
    const response = await apiClient.post(`/claim`, { jobId, guestToken });
    return response.data;
  },

  /** 진단 진행 상태 실시간 조회 (B/E progress 필드 포함) */
  getStatus: async (jobId: string): Promise<DiagnoseStatusResponse> => {
    const response = await apiClient.get(`/${jobId}/status`);
    return response.data;
  },

  /** 진단 결과 삭제 (본인만) */
  deleteJob: async (jobId: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/${jobId}`);
    return response.data;
  },

  /** 진단 점수 산정 근거, 24개 항목, PSI 메시지 등 상세 근거 조회 */
  getScoreDetails: async (jobId: string): Promise<DiagnoseScoreDetailsResponse> => {
    const res = await apiClient.get(`/${jobId}/score-details`);
    return res.data;
  },

  /** 회원 진단 이력 (버전/점수변화 포함) */
  getHistory: async (page = 0, size = 20): Promise<DiagnoseHistoryResponse> => {
    const response = await apiClient.get(`/history?page=${page}&size=${size}`);
    return response.data;
  },

  /** URL별 전체 버전 이력 */
  getVersionHistory: async (urlHash: string): Promise<DiagnoseVersionHistoryItem[]> => {
    const response = await apiClient.get(`/history/${urlHash}/versions`);
    return response.data;
  },
};
